import { useCallback } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useUpdateSimpleField } from 'builder/hooks/useUpdateSimpleField'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import Anchor from 'builder/components/Anchor'
import EditorTitle from 'builder/components/EditorTitle'
import { TunerEditorProvider } from 'builder/components/Tuner'
import { SectionNames, selectors } from 'builder/modules/resumeEditor'
import { i18n as I18n } from 'builder/utils/i18n'
import loaderImage from 'images/builder/resume-editor/editor-loader.svg'

import {
  EditorContent,
  EditorPanel,
  EditorScroll,
  EditorTitleWrapper,
  Loader,
  LoaderContainer,
  ResumeHeaderBottomSpace,
  UserNavigation,
} from '../styles'
import { JapaneseSectionsList } from './JapaneseSectionsList'
import { JapanesePersonalDetailsSection } from './PersonalDetails/JapanesePersonalDetailsSection'
import { CustomSection } from './Sections/CustomSection/CustomSection'
import { ProfessionalSummary } from './Sections/ProfessionalSummary/ProfessionalSummary'
import { Spacing } from './common/Spacing'

const RirekishoEditor: React.FC = () => {
  const levels = useTypedSelector(state => state.generalEditor.levels)
  const resumeTemplates = useTypedSelector(state => state.generalEditor.resumeTemplates)
  const isOnline = useTypedSelector(state => state.application.isOnline)
  const resume = useTypedSelector(selectors.resume)

  const { updateSimpleField } = useUpdateSimpleField()

  const handleResumeNameChange = useCallback(
    (value: string) => {
      updateSimpleField({ name: 'name', value })
      trackInternalEvent('rename_resume')
    },
    [updateSimpleField],
  )

  if (!resume || resumeTemplates.length === 0) {
    return (
      <EditorPanel>
        <LoaderContainer>
          <Loader src={loaderImage} />
        </LoaderContainer>
      </EditorPanel>
    )
  }

  return (
    <EditorPanel>
      {isOnline && <UserNavigation hasFeatures />}

      <EditorScroll>
        <EditorContent>
          <EditorTitleWrapper>
            <EditorTitle
              onChange={handleResumeNameChange}
              value={resume.name ? resume.name : I18n.t('builder.resume_editor.untitled')}
            />

            <Spacing height="16px" width="100%" />
          </EditorTitleWrapper>

          <ResumeHeaderBottomSpace />

          <TunerEditorProvider {...{ resume }}>
            <Anchor id={SectionNames.details} scrollOffsetTop={70}>
              <JapanesePersonalDetailsSection {...{ resume }} />
            </Anchor>

            <JapaneseSectionsList
              {...{
                resume,
                levels,
              }}
            />

            <ProfessionalSummary {...{ resume }} />
            <CustomSection />
          </TunerEditorProvider>
        </EditorContent>
      </EditorScroll>
    </EditorPanel>
  )
}

export default RirekishoEditor
